import { useCallback, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useEsri } from '../../esriHooks'

import { getMap, getView } from '../../map/selectors'
import { setSelectedParcels } from '../../map/actions'

import { getIsSearching, getHasSearched, getQuery, getResults, getShowResults } from '../selectors'
import { showResults, hideResults, startSearch, endSearch, clearSearch, addResults, setQuery, } from '../actions'

import { useLocator, searchAddresses } from './addressSearch'
import { ownerSearch } from './ownerSearch'
import { pinSearch } from './pinSearch'

import { SEARCH_RESULT_SYMBOLS, RESULT_TYPES } from '../constants'

export const useIsShowingResults = (mapId) => {
  const isShowingResults = useSelector(getShowResults(mapId))
  return isShowingResults
}

export const useShowResultsButton = (mapId) => {
  const dispatch = useDispatch()
  const results = useSelector(getResults)
  const isShowingResults = useSelector(getShowResults(mapId))

  const isVisible = results && results.length > 0 && !isShowingResults
  const onClick = useCallback(() => dispatch(showResults(mapId)), [dispatch, mapId])

  return [isVisible, { onClick }]
}

const buildSearch = (mapId, dispatch, query, locator, Point, Polygon) => async () => {
  if (locator && query.length > 1) {
    dispatch(startSearch())
    const add = (results) => dispatch(addResults(mapId, results))
    const search1 = searchAddresses(locator, Point, query, add)
    const search2 = ownerSearch(Polygon, query, add)
    const search3 = pinSearch(Polygon, query, add)
    Promise.all([search1, search2, search3]).then(() => {
      dispatch(endSearch())
    })
  }
}

export const useSearchInput = (mapId) => {
  const dispatch = useDispatch()
  const [ready, { Point, Polygon }] = useEsri(['esri/geometry/Point', 'esri/geometry/Polygon'])
  const locator = useLocator()
  const query = useSelector(getQuery)
  const isSearching = useSelector(getIsSearching)

  const runSearch = useMemo(() => 
    ready && buildSearch(mapId, dispatch, query, locator, Point, Polygon), 
    [mapId, dispatch, Point, Polygon, ready, query, locator])

  const actions = useMemo(() => ({
    onChange: (e) => dispatch(setQuery(e.target.value)),
    onKeyDown: (e) => e.key === 'Enter' && runSearch(),
    start: () => runSearch(),
    clear: () => dispatch(clearSearch())
  }), [dispatch, runSearch])
  
  return [{ query, isSearching }, actions]
}

export const useNoResults = () => {
  const resultGroups = useSelector(getResults)
  const hasSearched = useSelector(getHasSearched)

  return hasSearched && resultGroups.length === 0
}

export const useSearchResults = (mapId) => {
  const dispatch = useDispatch()
  const resultGroups = useSelector(getResults)
  const visible = useSelector(getShowResults(mapId))

  const hide = useCallback(() => dispatch(hideResults(mapId)), [dispatch, mapId])

  return [{ resultGroups, visible }, { hide }]
}

export const useSearchResultItem = (mapId, item) => {
  const { label, geometry, attributes } = item
  const dispatch = useDispatch()
  const hide = useCallback(() => dispatch(hideResults(mapId)), [dispatch, mapId])
  const view = useSelector(getView(1))

  const onClick = useCallback(() => {
    dispatch(setSelectedParcels([{
      geometry,
      attributes
    }]))
    view.goTo({
      target: geometry,
      zoom: 15
    })
    hide()
  }, [geometry, attributes, view, hide, dispatch])

  return [{ label }, { onClick }]
}

export const useSearchResultsLayer = (mapId) => {
  const [ready, { Graphic }] = useEsri(['esri/Graphic'])
  const map = useSelector(getMap(mapId))
  const view = useSelector(getView(mapId))

  // update layer
  const results = useSelector(getResults)
  const resultGraphics = useMemo(() => {
    if (ready && results && Graphic) {
      const graphics = []
      results.forEach(group => {
        if (group.id === RESULT_TYPES.ADDRESS) {
          group.results.forEach((item, index) => {
            const id = `${group.id}|${index}`
            const graphic = new Graphic({
              geometry: item.geometry,
              attributes: {
                id,
                label: item.label,
                ...item
              },
              popupTemplate: {
                title: item.label
              },
              symbol: SEARCH_RESULT_SYMBOLS.selected[group.id]
            })
            graphics.push(graphic)
          })
        }
      })
      return graphics
    }
    return []
  }, [results, ready, Graphic])

  return [{ map, view, graphics: resultGraphics }]
}